const pickField = (record: Record<string, unknown>, candidates: string[]): unknown => {
  const lowered = Object.fromEntries(
    Object.entries(record).map(([key, value]) => [key.trim().toLowerCase(), value])
  );
  for (const candidate of candidates) {
    const direct = record[candidate];
    if (direct != null && String(direct).trim() !== '') {
      return direct;
    }
    const loweredValue = lowered[candidate.trim().toLowerCase()];
    if (loweredValue != null && String(loweredValue).trim() !== '') {
      return loweredValue;
    }
  }
  return null;
};

const parseAmount = (value: unknown): number | null => {
  if (value == null) {
    return null;
  }
  const normalized = String(value).replace(/,/g, '').trim();
  if (!normalized) {
    return null;
  }
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? Math.abs(numeric) : null;
};

const formatTallyDate = (raw: unknown): string | undefined => {
  const value = String(raw ?? '').trim();
  if (!value) {
    return undefined;
  }
  const tallyMatch = value.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (tallyMatch) {
    return `${tallyMatch[3]}-${tallyMatch[2]}-${tallyMatch[1]}`;
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return value;
};

const mapVoucherToTransaction = (voucher: Record<string, unknown>) => {
  const amountFromField = parseAmount(
    pickField(voucher, ['AMOUNT', 'amount', 'Amount INR', 'amount inr'])
  );
  const debit = parseAmount(pickField(voucher, ['DEBIT', 'debit', 'Debit Amount', 'debit amount']));
  const credit = parseAmount(pickField(voucher, ['CREDIT', 'credit', 'Credit Amount', 'credit amount']));
  const amount = amountFromField ?? debit ?? credit;

  return {
    voucher_number: pickField(voucher, [
      'VOUCHERNUMBER',
      'voucher_number',
      'Voucher Number',
      'Voucher No',
      'voucher no',
    ]),
    voucher_date: formatTallyDate(
      pickField(voucher, ['DATE', 'date', 'Voucher Date', 'voucher date', 'VCHDATE'])
    ),
    voucher_type: pickField(voucher, ['VOUCHERTYPENAME', 'VCHTYPE', 'Voucher Type', 'voucher type']),
    narration: pickField(voucher, ['NARRATION', 'narration', 'Description', 'description']),
    ledger_name: pickField(voucher, ['LEDGERNAME', 'ledger_name', 'Ledger Name', 'ledger name']),
    party_name: pickField(voucher, [
      'PARTYLEDGERNAME',
      'party_name',
      'Party A/c Name',
      'Party Ledger Name',
      'Supplier',
      'supplier',
    ]),
    amount: amount != null ? String(amount) : undefined,
    quantity: pickField(voucher, ['QUANTITY', 'quantity', 'Qty', 'QTY']),
    unit: pickField(voucher, ['UNIT', 'unit', 'UOM', 'uom']),
    debit: debit != null ? String(debit) : undefined,
    credit: credit != null ? String(credit) : undefined,
    currency: 'INR',
  };
};

const parseJsonTransactions = (text: string): unknown[] => {
  const trimmed = text.trim();
  const parsed = JSON.parse(trimmed) as unknown;
  if (Array.isArray(parsed)) {
    return parsed;
  }
  if (
    parsed &&
    typeof parsed === 'object' &&
    'transactions' in parsed &&
    Array.isArray((parsed as { transactions: unknown[] }).transactions)
  ) {
    return (parsed as { transactions: unknown[] }).transactions;
  }
  throw new Error('JSON must be an array of transactions or an object with a transactions array.');
};

const parseCsvTransactions = (text: string): unknown[] => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length < 2) {
    throw new Error('CSV must include a header row and at least one data row.');
  }

  const headers = lines[0].split(',').map((header) => header.trim());
  return lines.slice(1).map((line) => {
    const values = line.split(',').map((value) => value.trim());
    return headers.reduce<Record<string, string>>((row, header, index) => {
      row[header] = values[index] ?? '';
      return row;
    }, {});
  });
};

const parseTallyXmlTransactions = (text: string): unknown[] => {
  if (typeof DOMParser === 'undefined') {
    return [];
  }

  const doc = new DOMParser().parseFromString(text, 'application/xml');
  const parserError = doc.querySelector('parsererror');
  if (parserError) {
    throw new Error('Could not parse the XML accounting export.');
  }

  const vouchers = Array.from(doc.querySelectorAll('VOUCHER, voucher'));
  const transactions = vouchers
    .map((voucherNode) => {
      const voucher: Record<string, unknown> = {};
      const vchType = voucherNode.getAttribute('VCHTYPE') || voucherNode.getAttribute('vchtype');
      if (vchType) {
        voucher.VOUCHERTYPENAME = vchType;
      }

      Array.from(voucherNode.children).forEach((child) => {
        const tag = child.tagName?.toUpperCase();
        const value = child.textContent?.trim();
        if (tag && value) {
          voucher[tag] = value;
        }
      });

      const ledgerEntry = Array.from(voucherNode.children).find((child) =>
        child.tagName?.toUpperCase().includes('ALLLEDGERENTRIES')
      );
      if (ledgerEntry) {
        const ledgerName = ledgerEntry.querySelector('LEDGERNAME, ledgername')?.textContent?.trim();
        const amount = ledgerEntry.querySelector('AMOUNT, amount')?.textContent?.trim();
        if (ledgerName && !voucher.LEDGERNAME) {
          voucher.LEDGERNAME = ledgerName;
        }
        if (amount && !voucher.AMOUNT) {
          voucher.AMOUNT = amount;
        }
      }

      return mapVoucherToTransaction(voucher);
    })
    .filter((row) => row.voucher_date && (row.amount || row.debit || row.credit));

  return transactions;
};

const readFileText = async (file: File): Promise<string> => {
  if (typeof file.text === 'function') {
    return file.text();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

const unsupportedBinaryFormatMessage = (filename: string) =>
  `File upload is not available on this API version. Export ${filename.toLowerCase().endsWith('.pdf') ? 'XML, CSV, or JSON' : 'XML or CSV'} from your accounting software, or ask your administrator to deploy the latest backend.`;

/** Parse common accounting export files in the browser when /import-accounting-file is unavailable. */
export const parseAccountingImportFileClientSide = async (file: File): Promise<unknown[]> => {
  const loweredName = file.name.toLowerCase();

  if (loweredName.endsWith('.xml')) {
    const text = await readFileText(file);
    const transactions = parseTallyXmlTransactions(text);
    if (transactions.length === 0) {
      throw new Error('No transactions were found in the XML export.');
    }
    return transactions;
  }

  if (loweredName.endsWith('.json') || loweredName.endsWith('.csv')) {
    const text = await readFileText(file);
    const transactions = loweredName.endsWith('.json')
      ? parseJsonTransactions(text)
      : parseCsvTransactions(text);
    if (transactions.length === 0) {
      throw new Error('No transactions were found in the selected file.');
    }
    return transactions;
  }

  if (loweredName.endsWith('.xlsx') || loweredName.endsWith('.xls') || loweredName.endsWith('.pdf')) {
    throw new Error(unsupportedBinaryFormatMessage(file.name));
  }

  const text = await readFileText(file);
  const trimmed = text.trim();
  if (!trimmed) {
    throw new Error('The selected file is empty.');
  }

  if (trimmed.startsWith('<')) {
    const transactions = parseTallyXmlTransactions(text);
    if (transactions.length > 0) {
      return transactions;
    }
  }

  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    return parseJsonTransactions(text);
  }

  const csvTransactions = parseCsvTransactions(text);
  if (csvTransactions.length > 0) {
    return csvTransactions;
  }

  throw new Error('Unsupported accounting import file format.');
};
